/**
 * playon-pdp.js  —  v2
 *
 * Fixes:
 *  1. Selling plan correctly applied for all frequencies (1/3/6-month).
 *     Delivery select `click` no longer bubbles into the panel handler.
 *     All plan IDs logged so you can verify Smartrr IDs in the console.
 *
 *  2. Supply-card "Save $X" badges hidden by default; visible only when
 *     Subscribe & Save is the active purchase option. Controlled via a
 *     `playon-subscribe-active` CSS class on the nearest product-page root.
 *
 *  3. Wrong variant bug fixed. playon-pdp.js directly resolves the matching
 *     variant from window.playonVariants (embedded by Liquid) and forces
 *     the form's hidden <input name="id"> to the correct value after every
 *     supply card click — regardless of whether the theme element reacts.
 */

(function () {
  'use strict';

  /* ─────────────────────────────────────────────────────────────
     LOGGER
  ───────────────────────────────────────────────────────────── */
  var LOG_PREFIX = '[PlayOn PDP]';
  function log() {
    var args = Array.prototype.slice.call(arguments);
    args.unshift(LOG_PREFIX);
    console.log.apply(console, args);
  }
  function warn() {
    var args = Array.prototype.slice.call(arguments);
    args.unshift(LOG_PREFIX + ' ⚠');
    console.warn.apply(console, args);
  }

  /* ─────────────────────────────────────────────────────────────
     UTILITY
  ───────────────────────────────────────────────────────────── */
  function ready(fn) {
    if (document.readyState !== 'loading') { fn(); }
    else { document.addEventListener('DOMContentLoaded', fn); }
  }

  /** Walk DOM upward to find the product-page element; fall back to body. */
  function getPdpRoot(el) {
    if (!el) return document.body;
    return (
      el.closest('product-page') ||
      el.closest('[data-js-product-component]') ||
      document.body
    );
  }

  /** Returns the hidden <input name="id"> inside the product form. */
  function getVariantIdInput() {
    var span = document.querySelector('[data-update-block="variant-id-main-product"]');
    if (!span) {
      warn('Could not find [data-update-block="variant-id-main-product"]');
      return null;
    }
    return span.querySelector('input[name="id"]');
  }

  function formatMoney(cents) {
    // Prefer Shopify's built-in formatter when available.
    if (window.Shopify && typeof window.Shopify.formatMoney === 'function') {
      return window.Shopify.formatMoney(cents, window.Shopify.money_format);
    }

    // Fallback: format using money_format string when possible.
    var format = (window.Shopify && window.Shopify.money_format)
      ? window.Shopify.money_format
      : '${{amount}}';

    function formatWithDelimiters(amount, precision, thousands, decimal) {
      precision = precision == null ? 2 : precision;
      thousands = thousands || ',';
      decimal = decimal || '.';
      var number = (amount / 100).toFixed(precision);
      var parts = number.split('.');
      parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
      return parts.join(decimal);
    }

    // Common Shopify placeholders we may encounter.
    var value;
    if (format.indexOf('{{amount_no_decimals}}') > -1) {
      value = formatWithDelimiters(cents, 0);
      return format.replace('{{amount_no_decimals}}', value);
    }
    if (format.indexOf('{{amount_with_comma_separator}}') > -1) {
      value = formatWithDelimiters(cents, 2, '.', ',');
      return format.replace('{{amount_with_comma_separator}}', value);
    }
    if (format.indexOf('{{amount_no_decimals_with_comma_separator}}') > -1) {
      value = formatWithDelimiters(cents, 0, '.', ',');
      return format.replace('{{amount_no_decimals_with_comma_separator}}', value);
    }

    // Default: {{amount}}
    value = formatWithDelimiters(cents, 2);
    return format.replace('{{amount}}', value);
  }

  function getSelectedSellingPlanId() {
    var select = document.querySelector('[data-playon-selling-plan-select]');
    if (select && select.value) return select.value;
    var option = document.querySelector('[data-playon-option].is-active');
    if (option && option.dataset && option.dataset.planId) return option.dataset.planId;
    return null;
  }

  function computeSubscribePrice(variantPriceCents, sellingPlanId) {
    var map = window.playonSellingPlanAdjustments || {};
    var adj = sellingPlanId ? map[String(sellingPlanId)] : null;
    var value = adj && typeof adj.value !== 'undefined' ? adj.value : window.playonSubDiscount;
    var valueType = adj && adj.value_type ? adj.value_type : (window.playonSubDiscountType || 'percentage');

    if (!value || value === 0 || valueType === 'none') return variantPriceCents;
    if (valueType === 'percentage') {
      return Math.round(variantPriceCents * (1 - value / 100));
    }
    if (valueType === 'fixed_amount') {
      return Math.max(variantPriceCents - value, 0);
    }
    return variantPriceCents;
  }

  function updatePurchaseOptionPrices(variant) {
    if (!variant || typeof variant.price === 'undefined') return;

    var priceCents = variant.price;
    var sellingPlanId = getSelectedSellingPlanId();

    // One-time price
    var onetimeEl = document.querySelector('[data-playon-onetime-price]');
    if (onetimeEl) onetimeEl.textContent = formatMoney(priceCents);

    // Subscribe discounted price
    var subPriceEl = document.querySelector('[data-playon-sub-price]');
    if (subPriceEl) {
      var discounted = computeSubscribePrice(priceCents, sellingPlanId);
      subPriceEl.textContent = formatMoney(discounted);
    }

    // Strikethrough "old" price should always match selected variant price
    var oldPriceEl = document.querySelector(
      '.playon-purchase-option--subscribe .playon-purchase-option__price-old'
    );
    if (oldPriceEl) oldPriceEl.textContent = formatMoney(priceCents);
  }

  function getActivePlanType() {
    var active = document.querySelector('[data-playon-option].is-active');
    return active && active.dataset ? active.dataset.planType : 'subscription';
  }

  function updateStickyCart(variant) {
    var sticky = document.querySelector('[data-playon-sticky-cart]');
    if (!sticky || !variant) return;

    var variantText = sticky.querySelector('[data-playon-sticky-variant-text]');
    var unitPrice   = sticky.querySelector('[data-playon-sticky-unit-price]');
    var atcText     = sticky.querySelector('[data-playon-sticky-atc-text]');

    if (variantText) variantText.textContent = variant.title || '';
    if (unitPrice) unitPrice.textContent = formatMoney(variant.price) + ' / bottle';

    if (atcText) {
      var planType = getActivePlanType();
      if (planType === 'subscription') {
        var spId = getSelectedSellingPlanId();
        var sub = computeSubscribePrice(variant.price, spId);
        atcText.textContent = 'Add To Cart - ' + formatMoney(sub) + '/MO';
      } else {
        atcText.textContent = 'Add To Cart - ' + formatMoney(variant.price);
      }
    }
  }

  /**
   * From window.playonVariants (injected by Liquid), find the variant
   * whose option values match the currently-checked supply card radios.
   */
  function resolveVariantFromRadios() {
    var variants = window.playonVariants;
    if (!variants || !variants.length) {
      warn('window.playonVariants unavailable — cannot resolve variant');
      return null;
    }

    var containers = Array.prototype.slice.call(
      document.querySelectorAll('[data-playon-supply-cards]')
    );
    var selectedValues = containers.map(function (c) {
      var checked = c.querySelector('.playon-supply-card__input:checked');
      return checked ? checked.value : null;
    });
    log('Resolving variant — selected values:', selectedValues);

    var optionKeys = ['option1', 'option2', 'option3'];
    var matched = null;
    variants.forEach(function (v) {
      if (matched) return;
      var ok = selectedValues.every(function (val, i) {
        return val === null || v[optionKeys[i]] === val;
      });
      if (ok) matched = v;
    });

    if (matched) {
      log('Resolved → id:', matched.id, '|', matched.title,
          '| available:', matched.available);
    } else {
      warn('No variant matched options:', selectedValues);
    }
    return matched;
  }

  /* ─────────────────────────────────────────────────────────────
     1. SUPPLY CARD SELECTION
  ───────────────────────────────────────────────────────────── */
  function initSupplyCards() {
    var containers = document.querySelectorAll('[data-playon-supply-cards]');
    log('initSupplyCards — found', containers.length, 'option group(s)');

    containers.forEach(function (container) {
      var cards = container.querySelectorAll('.playon-supply-card');

      cards.forEach(function (card) {
        card.addEventListener('click', function () {
          var radio = card.querySelector('.playon-supply-card__input');
          if (!radio || radio.disabled) return;

          log('Card click → value: "' + radio.value + '"');

          // 1. Visual state
          cards.forEach(function (c) { c.classList.remove('is-active'); });
          card.classList.add('is-active');

          // 2. Check the radio
          radio.checked = true;

          // 3. Notify theme's product-variants element
          radio.dispatchEvent(new Event('change', { bubbles: true }));
          radio.dispatchEvent(new Event('input',  { bubbles: true }));

          // 4. Direct variant-ID fallback — runs after theme element had a tick
          setTimeout(function () {
            var idInput = getVariantIdInput();
            log('Variant id input after theme tick:',
                idInput ? idInput.value : 'NOT FOUND');

            var resolved = resolveVariantFromRadios();
            if (!resolved) return;

            if (idInput && String(idInput.value) !== String(resolved.id)) {
              warn('Theme element did not update id. Forcing',
                   idInput.value, '→', resolved.id);
              idInput.value = String(resolved.id);
              idInput.dispatchEvent(new Event('change', { bubbles: true }));
            } else {
              log('✓ Variant id correct:', resolved.id);
            }
            updateBadgeAndPlansForVariant(resolved);
            updatePurchaseOptionPrices(resolved);
            updateStickyCart(resolved);
          }, 60);
        });

        // Sync visual state when Shopify checks the radio externally
        var radio = card.querySelector('.playon-supply-card__input');
        if (radio) {
          radio.addEventListener('change', function () {
            if (radio.checked) {
              var parent = radio.closest('[data-playon-supply-cards]');
              if (parent) {
                parent.querySelectorAll('.playon-supply-card').forEach(function (c) {
                  c.classList.remove('is-active');
                });
              }
              card.classList.add('is-active');
            }
          });
        }
      });
    });
  }

  /* ─────────────────────────────────────────────────────────────
     2. PURCHASE OPTION TOGGLE
  ───────────────────────────────────────────────────────────── */
  function initPurchaseOptions() {
    var wrappers = document.querySelectorAll('[data-playon-purchase-options]');
    log('initPurchaseOptions — found', wrappers.length, 'wrapper(s)');

    wrappers.forEach(function (wrapper) {
      var options    = wrapper.querySelectorAll('[data-playon-option]');
      var planInput  = wrapper.querySelector('[data-playon-selling-plan-input]');
      var planSelect = wrapper.querySelector('[data-playon-selling-plan-select]');
      var pdpRoot    = getPdpRoot(wrapper);

      // Log all available selling plans for debugging Issue #1
      log('--- Available selling plans ---');
      if (window.playonSellingPlans && window.playonSellingPlans.length) {
        window.playonSellingPlans.forEach(function (sp) {
          log('  id:', sp.id, '| name:', sp.name, '| group:', sp.groupName);
        });
      } else {
        warn('window.playonSellingPlans empty — check Liquid <script> tag in variant_picker block');
      }
      if (planInput) {
        log('Initial selling_plan value:', planInput.value,
            '| disabled:', planInput.disabled);
      }

      if (!options.length) return;

      options.forEach(function (option) {
        option.addEventListener('click', function (e) {
          // FIX: Don't re-run panel logic when click came from inside the <select>.
          // The select's own `change` listener handles the plan-ID update.
          if (e.target.closest('[data-playon-selling-plan-select]')) {
            log('Click from delivery select — panel handler skipped');
            return;
          }

          var type   = option.dataset.planType;
          var planId = option.dataset.planId || '';
          log('Option clicked — type:', type, '| fallback planId:', planId);

          // Toggle active state on all panels
          options.forEach(function (o) {
            o.classList.remove('is-active');
            var dot = o.querySelector('.playon-purchase-option__radio');
            if (dot) dot.classList.remove('is-selected');
          });
          option.classList.add('is-active');
          var activeDot = option.querySelector('.playon-purchase-option__radio');
          if (activeDot) activeDot.classList.add('is-selected');

          // FIX #2: badge visibility
          if (type === 'subscription') {
            pdpRoot.classList.add('playon-subscribe-active');
          } else {
            pdpRoot.classList.remove('playon-subscribe-active');
          }

          // Update selling_plan hidden input
          if (planInput) {
            if (type === 'subscription') {
              var selectedPlan = (planSelect && planSelect.value)
                ? planSelect.value
                : planId;
              planInput.value    = selectedPlan || planId;
              planInput.disabled = false;
              log('✓ selling_plan →', planInput.value,
                  '(from select:', !!(planSelect && planSelect.value), ')');
            } else {
              planInput.value    = '';
              planInput.disabled = true;
              log('✓ selling_plan cleared (one-time)');
            }
            planInput.dispatchEvent(new Event('change', { bubbles: true }));
          } else {
            warn('[data-playon-selling-plan-input] not found');
          }
        });
      });

      // Init: subscribe is active by default
      var subscribeOption = wrapper.querySelector('[data-plan-type="subscription"]');
      if (subscribeOption) {
        if (!subscribeOption.classList.contains('is-active')) {
          subscribeOption.classList.add('is-active');
          var dot = subscribeOption.querySelector('.playon-purchase-option__radio');
          if (dot) dot.classList.add('is-selected');
        }
        pdpRoot.classList.add('playon-subscribe-active');
        log('Subscribe initialised as active — playon-subscribe-active added to root');
      }
    });
  }

  /* ─────────────────────────────────────────────────────────────
     3. DELIVERY FREQUENCY SELECTOR
  ───────────────────────────────────────────────────────────── */
  function initDeliverySelectors() {
    var selects = document.querySelectorAll('[data-playon-selling-plan-select]');
    log('initDeliverySelectors — found', selects.length, 'select(s)');

    selects.forEach(function (select) {
      var selectorWrapper = select.closest('.playon-delivery-selector');
      var labelEl         = selectorWrapper
        ? selectorWrapper.querySelector('[data-playon-delivery-label]')
        : null;
      var planInput = document.querySelector('[data-playon-selling-plan-input]');

      // Log the initial options for debugging
      log('Delivery select options:');
      Array.prototype.forEach.call(select.options, function (opt) {
        log('  id:', opt.value, '| label:', opt.text);
      });

      select.addEventListener('change', function () {
        var selected = select.options[select.selectedIndex];
        log('Delivery select changed → id:', select.value,
            '| name:', selected ? selected.text : '?');

        if (labelEl && selected) {
          labelEl.textContent = selected.text;
        }

        if (planInput) {
          if (!planInput.disabled) {
            planInput.value = select.value;
            log('✓ selling_plan updated to:', planInput.value);
            planInput.dispatchEvent(new Event('change', { bubbles: true }));
          } else {
            warn('planInput is disabled (one-time mode) — not updating selling_plan');
          }
        } else {
          warn('[data-playon-selling-plan-input] not found on page');
        }

        // Also recompute subscribe price for the currently-selected variant.
        var idInput = getVariantIdInput();
        if (idInput && window.playonVariants) {
          var v = window.playonVariants.filter(function (vv) {
            return String(vv.id) === String(idInput.value);
          })[0];
          if (v) {
            updatePurchaseOptionPrices(v);
            updateStickyCart(v);
          }
        }
      });
    });
  }

  /* ─────────────────────────────────────────────────────────────
     4. CUSTOM QUANTITY SELECTOR
  ───────────────────────────────────────────────────────────── */
  function initQtySelectors() {
    var qtyWrappers = document.querySelectorAll('.playon-qty-selector');

    qtyWrappers.forEach(function (wrapper) {
      var input    = wrapper.querySelector('.playon-qty-selector__value');
      var btnMinus = wrapper.querySelector('.playon-qty-selector__btn:first-of-type');
      var btnPlus  = wrapper.querySelector('.playon-qty-selector__btn:last-of-type');
      if (!input) return;

      function getVal()  { return parseInt(input.value, 10) || 1; }
      function setVal(v) {
        var min = parseInt(input.min, 10) || 1;
        var max = parseInt(input.max, 10) || 999;
        input.value = Math.min(Math.max(v, min), max);
        input.dispatchEvent(new Event('change', { bubbles: true }));
        input.dispatchEvent(new Event('input',  { bubbles: true }));
      }

      if (btnMinus) btnMinus.addEventListener('click', function (e) { e.preventDefault(); setVal(getVal() - 1); });
      if (btnPlus)  btnPlus.addEventListener('click',  function (e) { e.preventDefault(); setVal(getVal() + 1); });

      input.addEventListener('keydown', function (e) {
        if (['-', '+', 'e', 'E', '.'].includes(e.key)) e.preventDefault();
      });
    });
  }

  /* ─────────────────────────────────────────────────────────────
     5. PLAYON ACCORDION
  ───────────────────────────────────────────────────────────── */
  function initPlayonAccordion() {
    var items = document.querySelectorAll('.playon-accordion-item');

    items.forEach(function (item) {
      var header  = item.querySelector('.playon-accordion-header');
      var content = item.querySelector('.playon-accordion-content');
      if (!header || !content) return;

      header.addEventListener('click', function () {
        var isOpen = item.classList.contains('is-open');
        var container = item.closest('.playon-specs');
        if (container) {
          container.querySelectorAll('.playon-accordion-item').forEach(function (i) {
            i.classList.remove('is-open');
            var h = i.querySelector('.playon-accordion-header');
            if (h) h.setAttribute('aria-expanded', 'false');
          });
        }
        if (!isOpen) {
          item.classList.add('is-open');
          header.setAttribute('aria-expanded', 'true');
        }
      });
    });
  }
    function initTestimonialsVideoLightbox() {
    var triggers = document.querySelectorAll('[data-playon-testimonial-video]');
    if (!triggers.length) return;

    triggers.forEach(function (btn) {
      btn.addEventListener('click', function () {
        var src = btn.getAttribute('data-video-src');
        if (!src) return;
        var poster = btn.getAttribute('data-video-poster') || '';

        var html = ''
          + '<div class="playon-video-lightbox">'
          + '  <button class="basicLightboxClose basicLightboxCloseWithIcon" aria-label="Close"></button>'
          + '  <video class="playon-video-lightbox__video" controls autoplay playsinline ' + (poster ? ('poster="' + poster + '"') : '') + '>'
          + '    <source src="' + src + '" type="video/mp4">'
          + '  </video>'
          + '</div>';

        var instance = window.basicLightbox && window.basicLightbox.create
          ? window.basicLightbox.create(html, {
              onClose: function (inst) {
                try {
                  var v = inst.element().querySelector('video');
                  if (v) {
                    v.pause();
                    v.removeAttribute('src');
                    v.load();
                  }
                } catch (e) {}
              }
            })
          : null;

        if (instance) instance.show();
      });
    });
  }

  /* ─────────────────────────────────────────────────────────────
     6. VARIANT CHANGE SYNC
  ───────────────────────────────────────────────────────────── */
  function initVariantChangeSync() {
    var variantsEls = document.querySelectorAll('product-variants[data-main-product-variants]');
    if (!variantsEls.length) {
      warn('No product-variants[data-main-product-variants] found — cannot sync prices');
      return;
    }

    variantsEls.forEach(function (pv) {
      pv.addEventListener('VARIANT_CHANGE', function (e) {
        var variant = e && e.target ? e.target.currentVariant : null;
        if (!variant) return;
        log('VARIANT_CHANGE → id:', variant.id, '|', variant.title, '| price:', variant.price);

        // Sync supply card visual state
        document.querySelectorAll('[data-playon-supply-cards]').forEach(function (container) {
          container.querySelectorAll('.playon-supply-card').forEach(function (card) {
            var radio = card.querySelector('.playon-supply-card__input');
            if (!radio) return;
            if (radio.checked) card.classList.add('is-active');
            else               card.classList.remove('is-active');
          });
        });

        updateBadgeAndPlansForVariant(variant);
        updatePurchaseOptionPrices(variant);
        updateStickyCart(variant);
      });
    });
  }

  function getVariantPlans(variantId) {
  var map = window.playonVariantSellingPlans || {};
  var entry = map[String(variantId)];
  return entry && entry.plans ? entry.plans : [];
}

function updateBadgeAndPlansForVariant(variant) {
  if (!variant) return;
  var plans = getVariantPlans(variant.id);
  var firstPlan = plans[0];

  // Rebuild the delivery dropdown to only this variant's eligible plans
  var select = document.querySelector('[data-playon-selling-plan-select]');
  if (select) {
    select.innerHTML = plans.map(function (p) {
      return '<option value="' + p.id + '">' + p.name + '</option>';
    }).join('');
  }

  var labelEl = document.querySelector('[data-playon-delivery-label]');
  if (labelEl && firstPlan) labelEl.textContent = firstPlan.name;

  // Keep the hidden selling_plan input + panel dataset in sync
  var planInput = document.querySelector('[data-playon-selling-plan-input]');
  var subscribeOption = document.querySelector('[data-plan-type="subscription"]');
  if (firstPlan) {
    if (subscribeOption) subscribeOption.dataset.planId = firstPlan.id;
    if (planInput && !planInput.disabled) {
      planInput.value = firstPlan.id;
      planInput.dispatchEvent(new Event('change', { bubbles: true }));
    }
  }

  // Update badge text/visibility
  document.querySelectorAll('.playon-purchase-option__badge').forEach(function (badge) {
    if (firstPlan && firstPlan.valueType === 'percentage' && firstPlan.value > 0) {
      badge.textContent = 'Get ' + firstPlan.value + '% Off';
      badge.style.display = '';
    } else {
      badge.style.display = 'none';
    }
  });

  // Keep the one-time "No X% Off" disclaimer in sync too
  var negText = document.querySelector('.playon-subscribe-feature--negative span');
  if (negText && firstPlan) {
    /*negText.textContent = 'No ' + firstPlan.value + '% Off On Future Orders';*/
    negText.textContent = 'No ' + '20' + '% Off On Future Orders';
  }

  // Keep price math (computeSubscribePrice) correct for the new plan id
  if (firstPlan) {
    window.playonSellingPlanAdjustments = window.playonSellingPlanAdjustments || {};
    window.playonSellingPlanAdjustments[String(firstPlan.id)] = {
      value: firstPlan.value,
      value_type: firstPlan.valueType
    };
  }
}

  function initStickyCart() {
    var sticky = document.querySelector('[data-playon-sticky-cart]');
    if (!sticky) return;

    var sectionId = sticky.getAttribute('data-section-id');
    var qtyInput = sectionId
      ? document.getElementById('playon-qty-' + sectionId)
      : null;
    if (!qtyInput) {
      // Fallback: find the quantity input in the main product form
      qtyInput = document.querySelector('.product-form input[name="quantity"]');
    }

    var qtyValue = sticky.querySelector('[data-playon-sticky-qty-value]');
    var btnMinus = sticky.querySelector('[data-playon-sticky-qty-minus]');
    var btnPlus  = sticky.querySelector('[data-playon-sticky-qty-plus]');

    function readQty() {
      return qtyInput ? (parseInt(qtyInput.value, 10) || 1) : 1;
    }
    function writeQty(v) {
      if (!qtyInput) return;
      var min = parseInt(qtyInput.min, 10) || 1;
      var max = parseInt(qtyInput.max, 10) || 999;
      qtyInput.value = Math.min(Math.max(v, min), max);
      qtyInput.dispatchEvent(new Event('change', { bubbles: true }));
      qtyInput.dispatchEvent(new Event('input', { bubbles: true }));
      if (qtyValue) qtyValue.textContent = String(qtyInput.value);
    }

    if (qtyValue) qtyValue.textContent = String(readQty());
    if (qtyInput) {
      qtyInput.addEventListener('change', function () {
        if (qtyValue) qtyValue.textContent = String(readQty());
      });
    }
    if (btnMinus) btnMinus.addEventListener('click', function (e) {
      e.preventDefault();
      writeQty(readQty() - 1);
    });
    if (btnPlus) btnPlus.addEventListener('click', function (e) {
      e.preventDefault();
      writeQty(readQty() + 1);
    });

    // Clicking the variant button should jump to the purchase area
    var variantBtn = sticky.querySelector('[data-playon-sticky-variant]');
    if (variantBtn) {
      variantBtn.addEventListener('click', function () {
        var target =
          document.querySelector('[data-playon-purchase-options]') ||
          document.querySelector('.playon-supply-picker') ||
          document.querySelector('[data-main-product-variants]');
        if (target) target.scrollIntoView({ behavior: 'smooth', block: 'start' });
      });
    }

    // Initial render from current variant id input
    var idInput = getVariantIdInput();
    if (idInput && window.playonVariants) {
      var v = window.playonVariants.filter(function (vv) {
        return String(vv.id) === String(idInput.value);
      })[0];
      if (v) updateStickyCart(v);
    }

    // When toggling purchase options, refresh sticky button text
    document.querySelectorAll('[data-playon-option]').forEach(function (opt) {
      opt.addEventListener('click', function () {
        var idInput2 = getVariantIdInput();
        if (idInput2 && window.playonVariants) {
          var v2 = window.playonVariants.filter(function (vv) {
            return String(vv.id) === String(idInput2.value);
          })[0];
          if (v2) updateStickyCart(v2);
        }
      });
    });
  }

  /* ─────────────────────────────────────────────────────────────
     7. FORM SUBMISSION DEBUG
  ───────────────────────────────────────────────────────────── */
  function initFormDebug() {
    var forms = document.querySelectorAll('[data-type="add-to-cart-form"]');
    log('initFormDebug — watching', forms.length, 'form(s)');

    forms.forEach(function (form) {
      form.addEventListener('submit', function () {
        var idInput  = form.querySelector('input[name="id"]');
        var spInput  = document.querySelector('[data-playon-selling-plan-input]');
        var qtyInput = form.querySelector('input[name="quantity"]');

        log('════ ADD TO CART SUBMIT ════');
        log('  variant id    :', idInput ? idInput.value : 'NOT FOUND ← bug!');
        log('  selling_plan  :',
            spInput
              ? (spInput.disabled ? '(omitted — one-time)' : spInput.value)
              : 'NOT FOUND');
        log('  quantity      :', qtyInput ? qtyInput.value : 'NOT FOUND');

        if (idInput && window.playonVariants) {
          var vMatch = window.playonVariants.filter(function (v) {
            return String(v.id) === String(idInput.value);
          })[0];
          log('  variant title :', vMatch ? vMatch.title : 'unknown (id not in playonVariants)');
        }
        if (spInput && !spInput.disabled && window.playonSellingPlans) {
          var pMatch = window.playonSellingPlans.filter(function (sp) {
            return String(sp.id) === String(spInput.value);
          })[0];
          if (pMatch) {
            log('  plan name     :', pMatch.name, '(group:', pMatch.groupName + ')');
          } else {
            warn('  selling_plan id', spInput.value, 'not found in playonSellingPlans — Smartrr may reject it');
          }
        }
        log('═══════════════════════════');
      });
    });
  }

  /* ─────────────────────────────────────────────────────────────
     INIT
  ───────────────────────────────────────────────────────────── */
  ready(function () {
    log('─── PlayOn PDP v2 starting ───');
    if (window.playonVariants) {
      log('playonVariants:', window.playonVariants.length, 'variant(s) loaded');
    } else {
      warn('playonVariants missing — add <script> tag in variant_picker block');
    }

    initSupplyCards();
    initPurchaseOptions();
    initDeliverySelectors();
    initQtySelectors();
    initPlayonAccordion();
    initTestimonialsVideoLightbox();
    initVariantChangeSync();
    initStickyCart();
    initFormDebug();

    log('─── Init complete ───');
  });

})();